import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { getJokerFieldType } from '../utils/helper';
import InputUI from '../components/Interface/InputUI';
import AutoCompleteSelectUI from '../components/Interface/AutoCompleteSelectUI';
import FormErrorUI from '../components/Interface/FormErrorUI';
import { Field } from 'formik';
import { JokerFieldTypes } from '../config/joker';

/**
 * Hook for generating joker columns, works from Formik context.
 * @param {Array} jokerLineFields - Joker field definitions
 * @param {string} key - Formik array key (e.g. 'productionItem')
 * @param {function} getValueIndex - Row index finder function
 * @returns {Array} DataGrid column definitions
 */
export default function useGenerateJokerCols(jokerLineFields, key, getValueIndex) {
  const { values, errors, setFieldValue, validateField } = useFormikContext();

  return useMemo(() => {
    return jokerLineFields.map((f) => {
      // Típus eldöntése: 'number', 'autocomplete', vagy 'string'
      const fieldType = getJokerFieldType(f);
      let columnType = 'string';
      if (fieldType === JokerFieldTypes.NUMBER) columnType = 'number';

      return {
        field: f.field,
        headerName: f.name,
        type: columnType,
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          if (fieldType === JokerFieldTypes.SELECT && Array.isArray(f.valuesToForm)) {
            const findedItem = f.valuesToForm.find((u) => u.value === values?.[key]?.[valueIndex]?.[f.field]);
            return findedItem?.label ?? '';
          }
          return values?.[key]?.[valueIndex]?.[f.field] ?? '';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          if (fieldType === JokerFieldTypes.SELECT && Array.isArray(f.valuesToForm)) {
            const findedItem = f.valuesToForm.find((u) => u.value === values?.[key]?.[valueIndex]?.[f.field]);
            return (
              <Field
                name={`${key}.${valueIndex}.${f.field}`}
                as={AutoCompleteSelectUI}
                fullWidth
                helperText={<FormErrorUI message={errors?.[key]?.[valueIndex]?.[f.field]} />}
                value={findedItem}
                onChange={(_e, newVal) => {
                  setFieldValue(`${key}.${valueIndex}.${f.field}`, newVal?.value ?? null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newVal?.value ?? null,
                  });
                }}
                dataset={f.valuesToForm}
                onBlur={async () => await validateField(`${key}.${valueIndex}.${f.field}`)}
              />
            );
          }
          if (fieldType === JokerFieldTypes.NUMBER) {
            return (
              <Field
                name={`${key}.${valueIndex}.${f.field}`}
                type="number"
                as={InputUI}
                variant="standard"
                fullWidth
                helperText={<FormErrorUI message={errors?.[key]?.[valueIndex]?.[f.field]} />}
                onChange={(e) => {
                  setFieldValue(`${key}.${valueIndex}.${f.field}`, e.target?.value);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: e.target?.value,
                  });
                }}
                onBlur={async () => await validateField(`${key}.${valueIndex}.${f.field}`)}
              />
            );
          }
          // Default: string mező
          return (
            <Field
              name={`${key}.${valueIndex}.${f.field}`}
              type="text"
              as={InputUI}
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.[key]?.[valueIndex]?.[f.field]} />}
              onChange={(e) => {
                setFieldValue(`${key}.${valueIndex}.${f.field}`, e.target?.value);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target?.value,
                });
              }}
              onBlur={async () => await validateField(`${key}.${valueIndex}.${f.field}`)}
            />
          );
        },
      };
    });
  }, [jokerLineFields, key, values, errors, setFieldValue, validateField, getValueIndex]);
}
